import { type MediaTarget } from '@/api/core/property-media';
import { isApiError } from '@/api/http/errors';
import { LoadFailure } from '@/components/load-failure';
import { LoadMoreFooter } from '@/components/load-more-footer';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { PropertyRuntime } from '../property-queries';
import { MediaItemsPanel } from './media-items-panel';
import { categoryLabel, languageLabel, purposeLabel } from './media-presentation';
import { propertyMediaGroupsQuery } from './media-queries';
import { MediaRetirementRecovery } from './media-retirement-form';
import { OwnMediaCreate } from './own-media-create';

export function NativeTypeMediaSection({
  readEnabled = true,
  runtime,
  generation,
  propertyId,
  typeId,
  typeName,
  readOnly = false,
}: {
  readEnabled?: boolean;
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  typeId: string;
  typeName: string;
  readOnly?: boolean;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  // 사진 추가를 눌러 연 묶음만 업로드 창을 자동으로 연다. 목록에서 묶음을 고르기만 할 때는 열지 않는다.
  const [addNonce, setAddNonce] = useState(0);
  const [createdGroupId, setCreatedGroupId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const target: MediaTarget = useMemo(
    () => ({ kind: 'ROOM_TYPE', id: typeId, floor: null }),
    [typeId],
  );
  const isCurrent = useCallback(() => {
    const session = runtime.session.getSnapshot();
    return session.status === 'authorized' && session.generation === generation;
  }, [runtime, generation]);
  const options = propertyMediaGroupsQuery(
    runtime,
    generation,
    propertyId,
    target,
    readEnabled,
    isCurrent,
    true,
  );
  const query = useInfiniteQuery(options);
  const pages = query.data?.pages;
  const latest = pages?.at(-1);
  const groups = pages?.flatMap((page) => page.groups) ?? [];
  const activeGroupId = groupId ?? groups[0]?.id ?? null;
  const bound = pages?.every(
    (page) =>
      page.scope.targetKind === 'ROOM_TYPE' &&
      page.scope.targetId?.toLowerCase() === typeId.toLowerCase(),
  );
  const nextError = query.isFetchNextPageError ? query.error : null;
  const cursorLost =
    isApiError(nextError) && (nextError.status === 409 || nextError.status === 422);
  const visibleCollectionId =
    bound &&
    activeGroupId &&
    (createdGroupId === activeGroupId || groups.some((g) => g.id === activeGroupId))
      ? activeGroupId
      : null;
  return (
    <section className="rounded-md border p-4">
      <h2 ref={heading} tabIndex={-1} className="font-semibold">
        {readOnly ? typeName : `${typeName} 사진`}
      </h2>
      <p className="body-sm text-muted-foreground mt-2">
        {readOnly ? '연결된 객실 유형에 등록된 사진입니다.' : '이 객실 유형에 직접 등록한 사진'}
      </p>
      {query.isFetching && !query.isPending && (
        <p role="status">사진 그룹을 새로 불러오고 있습니다.</p>
      )}
      <>
        {query.isPending ? (
          <p role="status" className="mt-3">
            사진 그룹을 불러오고 있습니다.
          </p>
        ) : null}
        {query.isError && !pages ? (
          <LoadFailure
            error={query.error}
            subject="사진 그룹"
            onRetry={() => void query.refetch()}
          />
        ) : null}
        {pages && !bound ? <p role="alert">사진 그룹의 대상을 확인할 수 없습니다.</p> : null}
        {bound && latest?.presentation === null && groups.length === 0 ? (
          <p className="mt-3">이 객실 유형에 등록된 사진 정보가 없습니다.</p>
        ) : null}
        {bound && latest?.presentation && groups.length === 0 ? (
          <p className="mt-3">이 객실 유형에 등록된 사진이 없습니다.</p>
        ) : null}
        {!readOnly && bound && latest ? (
          <OwnMediaCreate
            key={typeId}
            runtime={runtime}
            generation={generation}
            existingCollectionId={
              groups.find(
                (group) =>
                  group.id === (groupId ?? (groups.length === 1 ? groups[0]?.id : null)) &&
                  group.presentationId === null &&
                  group.collectionRevision !== null &&
                  group.collectionRevision > 0,
              )?.id
            }
            owner={{
              owner_scope_kind: 'ROOM_TYPE',
              owner_property_id: propertyId,
              owner_room_type_id: typeId,
              owner_room_id: null,
              owner_unit_id: null,
              purpose: 'GALLERY',
              language: 'ko',
              name: null,
              sort_order: groups.length,
              category_key: null,
            }}
            onSaved={async (id) => {
              setCreatedGroupId(id);
              setGroupId(id);
              setAddNonce((n) => n + 1);
              await queryClient.resetQueries({ queryKey: options.queryKey, exact: true });
            }}
          />
        ) : null}
        {bound && groups.length > 1 ? (
          <details className="my-3">
            <summary className="cursor-pointer">다른 사진 보기 · {groups.length}개 분류</summary>
            <ul className="mt-3 flex max-h-48 flex-wrap gap-2 overflow-auto" aria-label="사진 그룹">
              {groups.map((group) => (
                <li key={group.id}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCreatedGroupId(null);
                      setGroupId(group.id);
                      setAddNonce(0);
                    }}
                  >
                    {purposeLabel(group.purpose)} · {languageLabel(group.language)} ·{' '}
                    {categoryLabel(group.categoryName)} · 순서{' '}
                    {group.sourceOrder === null ? '없음' : group.sourceOrder + 1}
                  </Button>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        {bound && latest && (groups.length > 0 || query.hasNextPage) ? (
          <LoadMoreFooter
            label="사진 그룹"
            loaded={groups.length}
            total={latest.page.total}
            hasMore={query.hasNextPage}
            isFetching={query.isFetchingNextPage}
            unit="개 그룹"
            errorMessage={
              nextError
                ? cursorLost
                  ? '목록이 바뀌었습니다. 처음부터 다시 조회해 주세요.'
                  : '다음 그룹을 불러오지 못했습니다.'
                : null
            }
            restart={
              cursorLost
                ? {
                    label: '처음부터 다시 조회',
                    onClick: () =>
                      void queryClient.resetQueries({ queryKey: options.queryKey, exact: true }),
                  }
                : null
            }
            onLoadMore={() => void query.fetchNextPage()}
          />
        ) : null}
      </>
      {!readOnly && (
        <MediaRetirementRecovery
          runtime={runtime}
          generation={generation}
          propertyId={propertyId}
          target={target}
          isCurrent={isCurrent}
          visibleCollectionId={visibleCollectionId}
          onSaved={async (command) => {
            await queryClient.resetQueries({ queryKey: options.queryKey, exact: true });
            if (!command.itemId) {
              setGroupId((id) => (id === command.collectionId ? null : id));
              setCreatedGroupId((id) => (id === command.collectionId ? null : id));
            }
            heading.current?.focus();
          }}
        />
      )}
      {bound &&
      activeGroupId &&
      (createdGroupId === activeGroupId || groups.some((group) => group.id === activeGroupId)) ? (
        <MediaItemsPanel
          readEnabled={readEnabled}
          key={`${activeGroupId}:${String(addNonce)}`}
          runtime={runtime}
          generation={generation}
          propertyId={propertyId}
          groupId={activeGroupId}
          isCurrent={isCurrent}
          scopeLabel={() => typeName}
          onClose={() => {
            heading.current?.focus();
            setGroupId(null);
            setCreatedGroupId(null);
          }}
          expectedTarget={target}
          inline
          adding={!readOnly && addNonce > 0}
          nativeCurrent
          allowNativeWrite={!readOnly}
        />
      ) : null}
    </section>
  );
}
